import { format, subDays } from 'date-fns';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useRef, useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    H6Emphasized,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import { centsToFormattedDollars } from '../../common/utils/formatting';
import {
    useGetActiveCashJournals,
    useGetCashJournalsByDateRange,
} from '../hooks/useCashJournalData';
import type {
    AdminCashJournalTransferDto,
    CashJournalStatus,
} from '../types/ImOpsTypes';
import { CashJournalDetailsModal } from './CashJournalDetailsModal';

const getStatusColor = (status: CashJournalStatus): string => {
    switch (status) {
        case 'COMPLETED':
            return '#28a745';
        case 'INITIATED':
        case 'PROCESSING':
            return '#ffc107';
        case 'FAILED':
            return '#dc3545';
        case 'CANCELED':
        case 'SKIPPED':
            return '#6c757d';
    }
};

const ActiveJournalsTable = ({
    onRowClick,
}: {
    onRowClick: (row: AdminCashJournalTransferDto, refetch: () => void) => void;
}) => {
    const { data, isLoading, error, refetch } = useGetActiveCashJournals({});

    if (isLoading) {
        return <Spinner />;
    }

    if (error != null) {
        return (
            <BodySmallEmphasized color="$red1100">
                Error loading cash journals:{' '}
                {error instanceof Error ? error.message : String(error)}
            </BodySmallEmphasized>
        );
    }

    return (
        <YStack gap={8}>
            <XStack justifyContent="space-between" alignItems="center">
                <H6Emphasized>
                    Active Cash Journals ({data?.length ?? 0})
                </H6Emphasized>
                <Button
                    variant="secondary"
                    onPress={() => {
                        void refetch();
                    }}
                >
                    Refresh
                </Button>
            </XStack>
            <BodySmallRegular>
                Click a row to view details and take action.
            </BodySmallRegular>
            <CashJournalDataTable
                journals={data ?? []}
                onRowClick={(row) => {
                    onRowClick(row, () => {
                        void refetch();
                    });
                }}
                emptyMessage="No active cash journals"
            />
        </YStack>
    );
};

const DateRangeJournalsTable = ({
    onRowClick,
}: {
    onRowClick: (row: AdminCashJournalTransferDto, refetch: () => void) => void;
}) => {
    const today = new Date();
    const [fromDate, setFromDate] = useState<Date>(subDays(today, 7));
    const [toDate, setToDate] = useState<Date>(today);
    const [queryDates, setQueryDates] = useState<{
        fromDate: string;
        toDate: string;
        searchKey: number;
    } | null>(null);

    const fromDateStr = format(fromDate, 'yyyy-MM-dd');
    const toDateStr = format(toDate, 'yyyy-MM-dd');

    const { data, isLoading, error, refetch } = useGetCashJournalsByDateRange(
        queryDates ?? { fromDate: '', toDate: '', searchKey: 0 },
        { enabled: queryDates != null },
    );

    const handleSearch = () => {
        setQueryDates((prev) => ({
            fromDate: fromDateStr,
            toDate: toDateStr,
            searchKey: (prev?.searchKey ?? 0) + 1,
        }));
    };

    return (
        <YStack gap={12}>
            <H6Emphasized>Cash Journals by Date Range</H6Emphasized>
            <XStack gap={16} alignItems="center" flexWrap="wrap">
                <XStack alignItems="center" gap={8}>
                    <BodyMediumRegular>From:</BodyMediumRegular>
                    <Calendar
                        value={fromDate}
                        onChange={(e) => {
                            if (e.value instanceof Date) setFromDate(e.value);
                        }}
                        showIcon
                        dateFormat="mm/dd/yy"
                        aria-label="From date"
                        maxDate={toDate}
                    />
                </XStack>
                <XStack alignItems="center" gap={8}>
                    <BodyMediumRegular>To:</BodyMediumRegular>
                    <Calendar
                        value={toDate}
                        onChange={(e) => {
                            if (e.value instanceof Date) setToDate(e.value);
                        }}
                        showIcon
                        dateFormat="mm/dd/yy"
                        aria-label="To date"
                        minDate={fromDate}
                        maxDate={today}
                    />
                </XStack>
                <Button variant="primary" onPress={handleSearch}>
                    Search
                </Button>
            </XStack>

            {queryDates != null && (
                <>
                    {isLoading && <Spinner />}
                    {error != null && (
                        <BodySmallEmphasized color="$red1100">
                            Error:{' '}
                            {error instanceof Error
                                ? error.message
                                : String(error)}
                        </BodySmallEmphasized>
                    )}
                    {data != null && (
                        <YStack gap={8}>
                            <BodySmallRegular>
                                {data.length} journal
                                {data.length !== 1 ? 's' : ''} from{' '}
                                {queryDates.fromDate} to {queryDates.toDate}.
                                Click a row for details.
                            </BodySmallRegular>
                            <CashJournalDataTable
                                journals={data}
                                onRowClick={(row) => {
                                    onRowClick(row, () => {
                                        void refetch();
                                    });
                                }}
                                emptyMessage="No cash journals found for this date range"
                            />
                        </YStack>
                    )}
                </>
            )}
        </YStack>
    );
};

const CashJournalDataTable = ({
    journals,
    onRowClick,
    emptyMessage,
}: {
    journals: AdminCashJournalTransferDto[];
    onRowClick: (row: AdminCashJournalTransferDto) => void;
    emptyMessage: string;
}) => {
    const apexStateBodyTemplate = (row: AdminCashJournalTransferDto) => (
        <BodySmallEmphasized color={getStatusColor(row.status)}>
            {row.apexCurrentState ?? row.status}
        </BodySmallEmphasized>
    );

    const apexDetailsBodyTemplate = (row: AdminCashJournalTransferDto) => (
        <BodySmallRegular fontFamily="monospace">
            {row.apexCashJournalId ?? '-'}
        </BodySmallRegular>
    );

    const accountsBodyTemplate = (row: AdminCashJournalTransferDto) => (
        <YStack>
            <BodySmallRegular>
                <BodySmallEmphasized>From:</BodySmallEmphasized>{' '}
                {row.fromApexAccountNumber}
            </BodySmallRegular>
            <BodySmallRegular>
                <BodySmallEmphasized>To:</BodySmallEmphasized>{' '}
                {row.toApexAccountNumber}
            </BodySmallRegular>
        </YStack>
    );

    return (
        <DataTable
            value={journals}
            size="small"
            tableStyle={{ width: '100%', fontSize: '0.875rem' }}
            emptyMessage={emptyMessage}
            onRowClick={(e) => {
                // PrimeReact DataTable types e.data as unknown
                onRowClick(e.data as unknown as AdminCashJournalTransferDto);
            }}
            selectionMode="single"
            style={{ cursor: 'pointer' }}
        >
            <Column
                field="clientTransferId"
                header="Client Transfer ID"
                style={{ fontSize: '0.75rem' }}
                body={(row: AdminCashJournalTransferDto) => (
                    <BodySmallRegular fontFamily="monospace">
                        {row.clientTransferId.substring(0, 20)}...
                    </BodySmallRegular>
                )}
            />
            <Column
                field="membershipId"
                header="Membership"
                style={{ fontSize: '0.75rem' }}
                body={(row: AdminCashJournalTransferDto) => (
                    <BodySmallRegular>{row.membershipId}</BodySmallRegular>
                )}
            />
            <Column
                field="purpose"
                header="Purpose"
                style={{ fontSize: '0.75rem' }}
                body={(row: AdminCashJournalTransferDto) => (
                    <BodySmallRegular>{row.purpose}</BodySmallRegular>
                )}
            />
            <Column
                header="Accounts"
                style={{ fontSize: '0.75rem' }}
                body={accountsBodyTemplate}
            />
            <Column
                field="amountCents"
                header="Amount"
                style={{ fontSize: '0.75rem' }}
                body={(row: AdminCashJournalTransferDto) => (
                    <BodySmallRegular>
                        {row.amountCents != null
                            ? centsToFormattedDollars(row.amountCents)
                            : row.fullDisbursement
                              ? 'Full'
                              : '-'}
                    </BodySmallRegular>
                )}
            />
            <Column
                header="Apex State"
                style={{ fontSize: '0.75rem' }}
                body={apexStateBodyTemplate}
            />
            <Column
                header="Apex ID"
                style={{ fontSize: '0.75rem' }}
                body={apexDetailsBodyTemplate}
            />
            <Column
                field="createdAt"
                header="Created"
                style={{ fontSize: '0.75rem' }}
                body={(row: AdminCashJournalTransferDto) => (
                    <BodySmallRegular>
                        {new Date(row.createdAt).toLocaleString()}
                    </BodySmallRegular>
                )}
            />
        </DataTable>
    );
};

export const ImOpsCashJournalsPanel = () => {
    const [selectedJournal, setSelectedJournal] =
        useState<AdminCashJournalTransferDto | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const refetchRef = useRef<(() => void) | null>(null);

    const handleRowClick = (
        row: AdminCashJournalTransferDto,
        refetch: () => void,
    ) => {
        refetchRef.current = refetch;
        setSelectedJournal(row);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedJournal(null);
    };

    const handleActionSuccess = () => {
        refetchRef.current?.();
        handleCloseModal();
    };

    return (
        <YStack gap={32}>
            <XStack
                justifyContent="space-between"
                width="100%"
                alignItems="center"
                role="banner"
            >
                <H6Emphasized>Cash Journals</H6Emphasized>
            </XStack>

            <ActiveJournalsTable onRowClick={handleRowClick} />

            <DateRangeJournalsTable onRowClick={handleRowClick} />

            <CashJournalDetailsModal
                journal={selectedJournal}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onActionSuccess={handleActionSuccess}
            />
        </YStack>
    );
};
